import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckItemButton, CheckItemButtonProps } from 'Src/feature/check-item/CheckItemButton';

import { CheckItemSplitUI } from './useCheckSplit';

const sectionId = 'CheckItemSplitButton';

interface CheckItemButtonSelectableProps {
  checkItem: CheckItemSplitUI;
  onClick: (checkItem: CheckItemSplitUI) => void;
  someSelections: boolean;
}

/**
 * @description
 * - Button used w/ CheckSplit
 */
const CheckItemSplitButton: FC<React.PropsWithChildren<CheckItemButtonSelectableProps>> = ({ checkItem, onClick, someSelections, ...rest }) => {
  const { t } = useTranslation();

  // !! calculated checkItem.values object is necessary for view
  // - checkItem.values.gross     instead of checkItem.gross
  // - checkItem.values.quantity  instead of checkItem.quantity
  // - checkItem.values.splitBy   instead of checkItem.splitBy
  const { values, menuItem, seatNames } = checkItem;
  const { combinedName } = menuItem;

  // !! do not render !!
  if (!checkItem.can.edit) return null;

  // merge check item state w/ check item print state
  // - check item print state is a collection of multiple states (check item and check item modifier print states)
  const is: CheckItemButtonProps['is'] = {
    someSelections: someSelections,
    readonly: false,
    loading: false,
    hold: false,
    scheduled: false,
    error: false,
    sent: checkItem.status === 'Sent',
    selected: checkItem.is.selected,
    voided: Boolean(checkItem.is.voided),
  };

  const numerator = checkItem.split.quantity || values.quantity;
  const denominator = values.splitBy || 1;
  let quantity: string | number;
  if (numerator % denominator === 0) quantity = numerator / denominator;
  else quantity = t('checkItem.quantitySplitBy', { numerator, denominator });

  let modifiers: CheckItemButtonProps['modifiers'] = undefined;

  if (checkItem.modifiers && checkItem.modifiers.length) {
    modifiers = checkItem.modifiers?.map((modifier) =>
      // TODO: when ModifierCommand is fully hydrated
      //  - we can leverage ModifierCommand.type === 'prefix'
      //  - we can leverage ModifierCommand.type === 'suffix'
      //  - we can style the ModifierCommand.name with a strong tag like in the designs
      //  - currently we are only utilizing the ModifierOption.name which is applied to CheckItemModifier.modifierName
      // const commandName = modifier.modifierCmdName;
      ({
        name: modifier.name || modifier.groupName,
        cost: modifier.price ? modifier.price : undefined
      })
    );
  }

  return (
    // NOTE: we don't pass total to this component because price calculations for split check/item on client side are avoided
    <CheckItemButton
      {...rest}
      uuid={checkItem.id}
      discounts={undefined}
      name={combinedName}
      onSelectMulti={() => onClick(checkItem)}
      onSelectOne={() => onClick(checkItem)}
      is={is}
      quantity={quantity}
      modifiers={modifiers}
      seatNames={seatNames}
    />
  );
};

export default CheckItemSplitButton;
export type { CheckItemButtonSelectableProps };
export { sectionId };
